import { FormControl } from '@angular/forms';

import { CatsRecordTypeEnum, CatsStatusCodeEnum } from '@shared-enums/cats.enum';
import { limitDecimalDisplay, trimDecimalZeros } from '@shared-helpers/string-number-helpers';
import { IForemanCatsProperties, ISkfConfigEntity } from '@shared-interfaces/api';

import { ICreateProductionEquipment, IModifyCatsRecordQuantity } from '../models/cats-outbound.model';
import { DatahubService } from '../services/datahub.service';

export class ProductionEquipment {
  display = ''; //value that will show in the application.
  value = new FormControl<string>('', { nonNullable: true }); //actual sap value in reactive form control
  weDateTotal = ''; //tracks this objects total across all timesheets for the week
  workDateTotal = 0; //tracks this objects total across all timesheets for the day
  listener: any;
  catsRecord?: IForemanCatsProperties; //Cats record this item relates to.
  shouldUpload = false;
  previousValue: any; //use this to update the weekly total without having to recalc everything. stores value before change event.
  //fields straight from SAP
  billcode: string;
  skf: string;
  description: string;
  type = '';
  canDelete = false;
  id = '';
  position = 0;

  constructor(sapRecord: ISkfConfigEntity) {
    this.description = sapRecord.CustDesc;
    this.skf = sapRecord.SKF;
    this.billcode = sapRecord.BillCode;
  }

  activate(position: number, catsRecord?: IForemanCatsProperties): void {
    //don't use this until you activate it. I wanted to let the object be initialized without cats records.
    this.id = this.skf + position.toString();
    this.position = position;
    this.display = `${this.description} ${position.toString()}`;
    if (!catsRecord) {
      this.setControlValueAndListen('', false);
      return;
    }
    this.catsRecord = catsRecord;
    if (Number(catsRecord.Status) > CatsStatusCodeEnum.ReadyForApproval) {
      this.setControlValueAndListen(trimDecimalZeros(catsRecord.Quantity), true, 'alreadyApprovedError');
      return;
    }
    this.setControlValueAndListen(trimDecimalZeros(catsRecord.Quantity), false);
  }

  makeProductionEquipmentRecord(datahub: DatahubService): ICreateProductionEquipment {
    return {
      ZempCreatedBy: datahub.timesheet.findTokenEmployee(),
      Extdocumentno: datahub.extDocument.Documentno,
      Zmatnr: this.billcode,
      Employeenumber: datahub.timesheet.findTokenEmployee(),
      Quantity: this.value.value.trim() === '' ? '0' : this.value.value,
      Workdate: datahub.date.getOdataString(),
      RecOrder: datahub.contract.getServiceOrder(),
      Zrecordtype: CatsRecordTypeEnum.ProductionEquipment,
      Statkeyfig: this.skf,
    };
  }

  getCatRecords(datahub: DatahubService): ICreateProductionEquipment | IModifyCatsRecordQuantity | null {
    if (!this.shouldUpload) return null;
    if (this.catsRecord) return this.formatModify(this.catsRecord);
    else return this.makeProductionEquipmentRecord(datahub);
  }

  private setControlValueAndListen(value: string, isDisabled: boolean, error?: string): void {
    this.value = new FormControl<string>({ value: value, disabled: isDisabled }, { nonNullable: true });
    if (error) {
      const errorObj: Record<string, boolean> = {}; //errors have to be objects for reactive controls
      errorObj[error] = true;
      this.value.setErrors(errorObj);
    }
    this.previousValue = this.value.value;
    this.listener = this.value.valueChanges.subscribe((valChange) => {
      if (this.catsRecord) {
        this.catsRecord.Quantity = valChange.trim() === '' ? '0' : valChange;
      }
      this.updateTotals(); //when the form changes, we can update the cats record in memory. For equipment, the field is Catshours. Then flag the record for upload.
      this.shouldUpload = true;
    });
    this.updateTotals();
  }

  updateTotals(): void {
    this.workDateTotal = Number(this.value.value);
    this.weDateTotal = '';
  }

  findCounters(): string {
    if (this.catsRecord) return this.catsRecord.Counter;
    else return '';
  }

  setValue(value: string): void {
    this.value.setValue(value);
  }

  deleteSelf(skfArray: any[]): void {
    this.removeSelf(skfArray, true);
  }

  removeSelf(skfArray: ProductionEquipment[], shouldUnsubscribe = false): void {
    const index = skfArray.findIndex((x) => x.id === this.id);
    if (index < 0) return;
    skfArray.splice(index, 1);
    if (shouldUnsubscribe) this.listener.unsubscribe();
  }

  getGraphableValues() {
    return [
      this.display,
      this.value.value,
      '',
      '',
      trimDecimalZeros(limitDecimalDisplay(this.workDateTotal)),
      trimDecimalZeros(limitDecimalDisplay(this.weDateTotal)),
    ];
  }

  private formatModify(catsRecord: IForemanCatsProperties): IModifyCatsRecordQuantity {
    return {
      Counter: catsRecord.Counter,
      Quantity: this.value.value.trim() === '' ? '0' : this.value.value,
      Quantity_X: true,
    };
  }
}
